import * as React from "react";
import { useRoomChat } from "./SubscribeToRoom";
import { InlineChat } from "../Chat/InlineChat";
import { ChatMessage } from "../Chat/InlineChat/types";
import { useTranslation } from "react-i18next";
import { useAudio } from "../AudioProvider";
import { ProfileImage } from "../ProfileImage";
import { TextWithEmoji } from "../TextWithEmoji";
import { useCustomChatContext, useUser, useUserId } from "../UserProvider";
import { useLocalStorage } from "../../hooks/useStorage";
import { usePlayerProfile } from "../PlayerProfile";
import { useOnClickOutside } from "../../hooks/useOnClickOutside";
import classNames from "classnames";
import { ChatTheme } from "../../graphql/generated";
import { Tooltip } from "../Tooltip";
import chatIcon from "../../assets/message.svg";
import { SendGiftModal } from "../Chat/SendGift";
import { EditorStateProvider } from "../Chat/EditorState";
import styles from "../Chat/Chat.module.scss";
import { Messages } from "../Chat/Messages";
import { ErrorBoundary } from "../ErrorBoundary";
import MessageInput from "../Chat/Input";
import { MAX_CHAT_MESSAGE_LENGTH } from "../../config";
import { useMutation } from "../../hooks/useMutation";
import {
  PurchaseVirtualItemInChatMutation,
  PurchaseVirtualItemInChatMutationVariables,
} from "../Chat/graphql/PurchaseVirtualItemInChat.generated";
import { useCallback } from "react";
import { loader } from "graphql.macro";
import { Editor } from "draft-js";
import { useChatMessages } from "../Chat/hooks/useChatMessages";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import { Loader } from "../Loader";
import {
  SendRoomChatMessageMutation,
  SendRoomChatMessageMutationVariables,
} from "../Chat/graphql/SendRoomChatMessageMutation.generated";
import { useSubscription } from "../../hooks/useSubscription";
import {
  JoinRoomSubscription,
  JoinRoomSubscriptionVariables,
} from "../Chat/graphql/JoinRoomSubscription.generated";
import { RoomChatMessagesFragmentFragment } from "./graphql/RoomChatMessagesFragment.generated";
import BlockedChats from "../Chat/BlockedChats";

const SOUND_PLAY_THRESHOLD = 5000;
const MESSAGES_CONTAINER = { height: "100%", paddingBottom: "40px" };
const SCROLL_THRESHOLD = MESSAGES_CONTAINER.height;
const PURCHASE_GIFT_MUTATION = loader(
  "../Chat/graphql/PurchaseVirtualItemInChat.graphql"
);
const SEND_ROOM_CHAT_MESSAGE_MUTATION = loader(
  "../Chat/graphql/SendRoomChatMessageMutation.graphql"
);
const JOIN_ROOM_SUBSCRIPTION = loader(
  "../Chat/graphql/JoinRoomSubscription.graphql"
);

const isChatMessages = (data: {
  __typename?: string;
}): data is RoomChatMessagesFragmentFragment => {
  return (
    (data as RoomChatMessagesFragmentFragment).__typename === "RoomChatMessages"
  );
};

const LastMessageContent = React.memo<{
  message?: ChatMessage;
  userId: string;
}>(({ message, userId }) => {
  const { t } = useTranslation();
  const { play } = useAudio();
  const lastPlayed = React.useRef(Date.now());
  React.useEffect(() => {
    const now = Date.now();
    if (
      message === undefined ||
      message.player.id === userId ||
      now - lastPlayed.current < SOUND_PLAY_THRESHOLD
    )
      return;

    play("incoming-message");
    lastPlayed.current = now;
  }, [message, play, userId]);
  if (message === undefined)
    return (
      <span className="text-sm last-message-text">
        {t("no_recent_chat_messages")}
      </span>
    );
  else
    return (
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <ProfileImage
            className="bg-gray-400 border rounded-full"
            id={message.player.id}
            name={message.player.name}
          />
        </div>
        <div className="px-1 text-sm truncate last-message-text">
          <TextWithEmoji text={message.message} />
        </div>
      </div>
    );
});

export function RoomChat({ roomId }: { roomId: string }) {
  const {
    messages,
    fetchMessagesMore,
    chatMessageData,
    handleSetNewMessage,
    currentId,
    setCurrentId,
  } = useChatMessages();

  const userId = useUserId();
  const { user } = useUser();
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [active, setActive] = React.useState(false);
  const [muted, setMuted] = useLocalStorage("chat_muted", false);
  const { t } = useTranslation();
  const { setOpenedChat } = useCustomChatContext();
  const inputRef = React.useRef<Editor | null>(null);
  const messagesRef = React.useRef<HTMLDivElement | null>(null);
  const [showGiftSelector, setShowGiftSelector] = React.useState(false);

  const showProfile = usePlayerProfile();
  const onProfileClick = React.useCallback(
    (profile: { id: string; name: string }) => {
      //@ts-ignore
      showProfile({ ...profile });
      return true;
    },
    [showProfile, roomId]
  );

  React.useEffect(() => {
    if (roomId) {
      setCurrentId(roomId);
    }
  }, [roomId]);

  const [{}, sendRoomMessage] = useMutation<
    SendRoomChatMessageMutation,
    SendRoomChatMessageMutationVariables
  >(SEND_ROOM_CHAT_MESSAGE_MUTATION);

  const onInput = (message: string) => {
    sendRoomMessage({
      input: {
        roomId: roomId,
        message: message,
      },
    }).then((chatData) => {
      if (chatData?.data?.sendRoomChatMessage) {
        handleSetNewMessage([
          {
            id: chatData.data?.sendRoomChatMessage?.id!,
            player: {
              id: chatData.data?.sendRoomChatMessage?.player?.id!,
              name: chatData.data?.sendRoomChatMessage?.player?.name!,
            },
            created: chatData.data?.sendRoomChatMessage?.created!,
            message: chatData.data?.sendRoomChatMessage?.message!,
            isReported: false,
          },
        ]);
      }
    });
  };

  const loadingRef = useInfiniteScroll<HTMLDivElement>(
    { root: messagesRef, rootMargin: "50px" },
    async () => {
      const endCursor = chatMessageData?.nextCursor ?? null;
      if (endCursor === null) return Promise.reject();
      return fetchMessagesMore(endCursor);
    }
  );

  useOnClickOutside(
    containerRef,
    React.useCallback(() => setActive(false), []),
    active
  );

  const [, sendGift] = useMutation<
    PurchaseVirtualItemInChatMutation,
    PurchaseVirtualItemInChatMutationVariables
  >(PURCHASE_GIFT_MUTATION);

  React.useEffect(() => {
    if (!active || inputRef.current === null) return;
    inputRef.current.focus();
  }, [active]);

  React.useEffect(() => {
    if (messagesRef.current === null) return;

    const remaining = Math.max(
      0,
      messagesRef.current.scrollHeight -
        messagesRef.current.scrollTop -
        messagesRef.current.offsetHeight
    );

    if (remaining < +SCROLL_THRESHOLD.replace("px", ""))
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
  }, [messages]);

  React.useLayoutEffect(() => {
    if (messagesRef.current === null || !active) return;
    messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
  }, [active]);

  const [{ data: subscriptionData }] = useSubscription<
    JoinRoomSubscription,
    JoinRoomSubscription,
    JoinRoomSubscriptionVariables
  >({
    query: JOIN_ROOM_SUBSCRIPTION,
    variables: { id: currentId },
    pause: !currentId,
  });

  React.useEffect(() => {
    if (subscriptionData === undefined || subscriptionData.roomData === null)
      return;

    const { roomData } = subscriptionData;

    if (isChatMessages(roomData)) {
      const messages = roomData.messages ?? [];

      if (roomData.messages.length === 1) handleSetNewMessage(messages);
    }
  }, [subscriptionData]);

  const onSendGift = useCallback(
    async ({ item, logo }: { item: string; logo: string }) => {
      const imgUrl = `${process.env.REACT_APP_API_HOST}/virtual-items/${logo}`;

      if (roomId) {
        await sendGift({ input: { chatId: roomId, item } }).then((data) => {
          onInput(imgUrl);
          setShowGiftSelector(false);
        });
      }
    },
    []
  );

  const handleSendGiftWindow = (val: boolean) => {
    setShowGiftSelector(val);
  };

  return (
    <div
      ref={containerRef}
      className={classNames("relative", {
        "theme-dark": user.chatTheme === ChatTheme.Dark,
        "theme-light": user.chatTheme === ChatTheme.Light,
      })}
    >
      <Tooltip label={t("chat")}>
        <div className={styles.rightHeaderIcon}>
          <img
            src={chatIcon}
            alt="Chat"
            onClick={() => {
              if (!muted) setActive(!active);
              // if (!active) getToken();
              setOpenedChat(false);
            }}
          />
        </div>
      </Tooltip>
      {active ? (
        <div
          className="absolute z-10 w-full"
          style={{ minWidth: "280px", height: "340px" }}
        >
          {showGiftSelector ? (
            <SendGiftModal
              name={""}
              close={() => setShowGiftSelector(false)}
              onSendGift={onSendGift}
            />
          ) : active ? (
            <EditorStateProvider>
              {active && (
                <div
                  style={{
                    background: "#1459cc",
                    borderRadius: "0 0 3px 3px",
                    height: "100%",
                    padding: "10px",
                  }}
                >
                  {user.chatBlocked ? (
                    <BlockedChats />
                  ) : (
                    <>
                      <div
                        style={MESSAGES_CONTAINER}
                        ref={messagesRef}
                        className={classNames(
                          styles.scrollable,
                          "flex",
                          "flex-col-reverse",
                          "border",
                          "border-gray-300",
                          "bg-gray-100",
                          "overflow-x-hidden",
                          "overflow-y-auto"
                        )}
                      >
                        <Messages
                          container={messagesRef}
                          onProfileClick={onProfileClick}
                          currentUserId={roomId}
                          messages={messages || []}
                        />
                        {chatMessageData?.hasMore && (
                          <div
                            className="flex w-full justify-center items-center py-2"
                            ref={loadingRef}
                          >
                            <Loader description={""} />
                          </div>
                        )}
                      </div>
                      <div
                        className="px-2 py-1 rounded-b"
                        style={{
                          position: "absolute",
                          left: "14px",
                          bottom: "10px",
                          borderRadius: "20px",
                          width: "calc(100% - 28px)",
                        }}
                      >
                        <ErrorBoundary fallback={null}>
                          <MessageInput
                            maxLength={MAX_CHAT_MESSAGE_LENGTH}
                            ref={inputRef}
                            onInput={onInput}
                            onSendGiftOpen={handleSendGiftWindow}
                            isSendGiftOpen={showGiftSelector}
                          />
                        </ErrorBoundary>
                      </div>
                    </>
                  )}
                </div>
              )}
            </EditorStateProvider>
          ) : (
            <></>
          )}
        </div>
      ) : (
        <></>
      )}
    </div>
  );
}
